import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { PowerOlxV2Tab } from '../../../../components/tabs/enums/tabs.enum';
import { OlxBalance, OlxItem, PowerOlxV2Response } from '../interfaces/power-olx-v2.interface';
import { Tab } from '../../../../components/tabs/interfaces/tab.interface';
import { Channel } from '../../channel/components/channel/channel.interface';
import { CardItemState } from '../interfaces/power-olx-tab-item.interface';

@Injectable({
  providedIn: 'root',
})
export class PowerOlxStateService {
  private readonly selectedPlannerGroupIdSubject$ = new BehaviorSubject<string | undefined>(undefined);
  readonly selectedPlannerGroupId$ = this.selectedPlannerGroupIdSubject$.asObservable();

  setPlannerGroupId(groupId?: string) {
    this.selectedPlannerGroupIdSubject$.next(groupId);
  }

  private readonly selectedTabSubject$ = new BehaviorSubject<Tab<PowerOlxV2Tab> | undefined>(undefined);
  readonly selectedTab$ = this.selectedTabSubject$.asObservable();

  setTab(tab: Tab<PowerOlxV2Tab>) {
    this.selectedTabSubject$.next(tab);
  }

  private readonly channelsSubject$ = new BehaviorSubject<Channel[] | undefined>(undefined);
  readonly channels$ = this.channelsSubject$.asObservable();

  setChannels(channels: Channel[]) {
    this.channelsSubject$.next(channels);
  }

  private readonly selectedChannelIdSubject$ = new BehaviorSubject<string | undefined>(undefined);
  readonly selectedChannelId$ = this.selectedChannelIdSubject$.asObservable();

  setSelectedChannelId(id: string) {
    this.selectedChannelIdSubject$.next(id);
  }

  private readonly dataSourceSubject$ = new BehaviorSubject<PowerOlxV2Response<OlxItem> | undefined>(undefined);
  readonly dataSource$ = this.dataSourceSubject$.asObservable();

  setDataSource(data: PowerOlxV2Response<OlxItem>) {
    this.dataSourceSubject$.next(data);
  }

  updateItems(updater: (items: OlxItem[]) => OlxItem[]) {
    const data = this.dataSourceSubject$.getValue();
    if (!data) return;

    const items = updater([...(data.items || [])]);
    this.dataSourceSubject$.next({
      ...data,
      items,
    });
  }

  private readonly balanceSubject$ = new BehaviorSubject<OlxBalance | undefined>(undefined);
  readonly balance$ = this.balanceSubject$.asObservable();

  setBalance(balance: OlxBalance) {
    this.balanceSubject$.next(balance);
  }

  private readonly reloadItemsSubject$ = new Subject<void>();
  readonly reloadItems$ = this.reloadItemsSubject$.asObservable();

  reloadItems() {
    this.reloadItemsSubject$.next();
  }

  private readonly reloadBalanceSubject$ = new Subject<void>();
  readonly reloadBalance$ = this.reloadBalanceSubject$.asObservable();

  reloadBalance() {
    this.reloadBalanceSubject$.next();
  }

  private readonly clearSelectionSubject$ = new Subject<void>();
  readonly clearSelection$ = this.clearSelectionSubject$.asObservable();

  clearSelection() {
    this.clearSelectionSubject$.next();
  }

  private readonly cardItemStateSubject$ = new Subject<{ id: number, state: CardItemState }>();
  readonly cardItemState$ = this.cardItemStateSubject$.asObservable();

  updateCardItemState(id: number, state: CardItemState) {
    this.cardItemStateSubject$.next({ id, state });
  }

  private readonly activeCardIdsSubject$ = new BehaviorSubject<(string | number)[]>([]);
  readonly activeCardIds$ = this.activeCardIdsSubject$.asObservable();

  setActiveCardIds(ids: (string | number)[] = []) {
    this.activeCardIdsSubject$.next(ids);
  }
}
